import { useCallback, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';

import { getSession, isEnokiWallet } from '@mysten/enoki';

import { useZkLoginWallet } from './useZkLoginWallet';

type UseEnokiSessionExpiryOptions = {
	onExpire?: () => Promise<void> | void;
};

export function useEnokiSessionExpiry({ onExpire }: UseEnokiSessionExpiryOptions) {
	const { currentWallet: wallet } = useZkLoginWallet();
	const onExpireRef = useRef(onExpire);
	const expiryHandledRef = useRef(false);
	const hadEnokiWalletRef = useRef(false);
	const cookieKey = 'suigar.enoki.session';
	const [cookies, setCookie, removeCookie] = useCookies([cookieKey]);
	const sessionCookie = cookies?.[cookieKey];
	const [hasCheckedSession, setHasCheckedSession] = useState(false);

	useEffect(() => {
		onExpireRef.current = onExpire;
	}, [onExpire]);

	const handleSessionExpiry = useCallback(async () => {
		if (expiryHandledRef.current) return;
		expiryHandledRef.current = true;
		try {
			await onExpireRef.current?.();
		} catch (error) {
			console.error('Failed to handle Enoki session expiry', error);
		}
	}, []);

	useEffect(() => {
		let cancelled = false;

		const hasEnokiWallet = Boolean(wallet && isEnokiWallet(wallet));

		if (!hasEnokiWallet) {
			if (hadEnokiWalletRef.current) {
				removeCookie(cookieKey, { path: '/' });
			}
			hadEnokiWalletRef.current = false;
			setHasCheckedSession(false);
			expiryHandledRef.current = false;
			return;
		}

		hadEnokiWalletRef.current = true;
		expiryHandledRef.current = false;
		setHasCheckedSession(false);

		const fetchSession = async () => {
			try {
				const session = await getSession(wallet!);
				if (cancelled) return;
				const expiry = typeof session?.expiresAt === 'number' ? session.expiresAt : null;
				if (!expiry || expiry <= Date.now()) {
					await handleSessionExpiry();
					return;
				}
				setCookie(cookieKey, session?.maxEpoch, { path: '/', expires: new Date(expiry), sameSite: 'lax' });
			} catch (err) {
				console.warn('Failed to retrieve Enoki session: ', err);
				await handleSessionExpiry();
			} finally {
				if (!cancelled) {
					setHasCheckedSession(true);
				}
			}
		};

		void fetchSession();

		return () => {
			cancelled = true;
		};
	}, [wallet, removeCookie, cookieKey, setCookie, handleSessionExpiry]);

	useEffect(() => {
		if (!wallet || !isEnokiWallet(wallet)) return;
		if (!sessionCookie && hasCheckedSession) {
			void handleSessionExpiry();
		}
	}, [wallet, sessionCookie, hasCheckedSession, handleSessionExpiry]);

	return wallet && !sessionCookie;
}
